import React, { useState, useEffect } from 'react';
import { X, Save, Loader, Calendar, AlertTriangle } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions, PERMISSIONS } from './usePermissions';

const ServiceAssignment = ({ isOpen, onClose, patient, config, onServiceAssigned, currentUser }) => {
  const { hasPermission } = usePermissions(currentUser);
  
  const [services, setServices] = useState([]);
  const [patientServices, setPatientServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorModal, setErrorModal] = useState(null);
  
  const [selectedService, setSelectedService] = useState(null);
  const [lastCompletedDate, setLastCompletedDate] = useState('');
  const [notes, setNotes] = useState('');

  const t = config?.terminology || {
    service: { singular: 'Service', plural: 'Services', singularLower: 'service', pluralLower: 'services' }
  };

  useEffect(() => {
    if (isOpen) {
      loadServices();
      // Set default last completed date to today
      setLastCompletedDate(new Date().toISOString().split('T')[0]);
    }
  }, [isOpen]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const [allServices, patientSvcs] = await Promise.all([
        ApiService.getServices(),
        ApiService.getPatientServices(patient.patientId)
      ]);
      setServices(allServices);
      setPatientServices(patientSvcs);
    } catch (err) {
      setError('Failed to load services');
      setErrorModal({ title: 'Error Loading Services', message: err.message || 'Failed to load services. Please try again.' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter out already assigned services
  const availableServices = services.filter(svc => 
    !patientServices.some(ps => ps.serviceId === svc.serviceId)
  );

  const calculateNextDueDate = (lastCompleted, intervalMonths) => {
    const date = new Date(lastCompleted);
    const months = parseInt(intervalMonths) || 0;
    
    // Add months properly to avoid overflow issues
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();
    
    date.setFullYear(currentYear + Math.floor((currentMonth + months) / 12));
    date.setMonth((currentMonth + months) % 12);
    
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const serviceData = {
        serviceId: selectedService.serviceId,
        serviceName: selectedService.name,
        intervalMonths: selectedService.intervalMonths,
        lastCompletedDate: lastCompletedDate,
        reminderDaysBefore: selectedService.reminderDaysBefore,
        notes: notes
      };

      await ApiService.addServiceToPatient(patient.patientId, serviceData);
      onServiceAssigned();
      handleClose();
    } catch (err) {
      setError('Failed to assign service');
      setErrorModal({ title: 'Error Assigning Service', message: err.message || 'Failed to assign service. Please try again.' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedService(null);
    setLastCompletedDate('');
    setNotes('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Check permission
  if (!hasPermission(PERMISSIONS.PATIENTS_SERVICES_ADD)) {
    return null; // Don't show modal if user can't add services
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-spruce-800 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Add {t.service.singular}
              </h2>
              <p className="text-orange-100 text-sm">
                {patient.firstName} {patient.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {!selectedService ? (
            /* Service Selection */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select {t.service.singular} <span className="text-red-500">*</span>
                </label>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader className="w-6 h-6 text-orange-600 animate-spin" />
                  </div>
                ) : availableServices.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    {services.length === 0 ? (
                      <>
                        No {t.service.pluralLower} available.
                        <br />
                        Please create a {t.service.singularLower} first.
                      </>
                    ) : (
                      <>
                        All available {t.service.pluralLower} have already been assigned to this patient.
                      </>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {availableServices.map((service) => (
                      <button
                        key={service.serviceId}
                        type="button"
                        onClick={() => setSelectedService(service)}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                      >
                        <div className="font-semibold text-gray-900">{service.name}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Every {service.intervalMonths} month{service.intervalMonths !== 1 ? 's' : ''}
                        </div>
                        {service.reminderDaysBefore && (
                          <div className="text-xs text-gray-500 mt-1">
                            Reminder: {service.reminderDaysBefore} days before
                          </div>
                        )}
                        {service.category && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                            {service.category}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Service Configuration */
            <div className="space-y-4">
              {/* Selected Service Display */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-700 font-medium">Selected {t.service.singular}</p>
                    <p className="text-lg font-bold text-orange-900">{selectedService.name}</p>
                    <p className="text-sm text-orange-700 mt-1">
                      Frequency: Every {selectedService.intervalMonths} month{selectedService.intervalMonths !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedService(null)}
                    className="text-sm text-orange-600 hover:text-orange-800 underline"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Last Completed Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Completed Date <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  When was this {t.service.singularLower} last completed? (Sets the next due date)
                </p>
                <input
                  type="date"
                  value={lastCompletedDate}
                  onChange={(e) => setLastCompletedDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>

              {/* Next Due Date Preview */}
              {lastCompletedDate && (
                <div className="bg-spruce-50 border border-spruce-200 rounded-lg p-4">
                  <p className="text-sm text-spruce-700 font-medium">Next Due Date</p>
                  <p className="text-lg font-bold text-spruce-900">
                    {new Date(calculateNextDueDate(lastCompletedDate, selectedService.intervalMonths)).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-xs text-spruce-600 mt-1">
                    Reminder will be sent {selectedService.reminderDaysBefore} days before
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="3"
                  placeholder="Any additional notes about this service..."
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            {selectedService && (
              <button
                type="submit"
                disabled={saving || !lastCompletedDate}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Add {t.service.singular}
                  </>
                )}
              </button>
            )}
          </div>
        </form>

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ServiceAssignment;